import React, { useCallback } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useNavigate } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';

import { Pills, Separator } from '@ajaibid/components';
import { Text } from 'Component/Ajaib';
import { FormatPercentage } from 'Component/NumberFormat';
import { Loader } from 'Component/Loader';

import useNewsFeedList from '@services/stock/global/news/feed';
import { useLoginPin } from '@hooks/auth';
import { useUserData } from '@services/users/me';

import { NewsItemPills, NewsItemPillsText, NewsItemPillsTextNeutral } from '@screens/News/style';
import ChevronDropRight from '@assets/Icon/svg/ChevronDropRight';
import useEventTracker from '../tracker';
import stylesGlobal from '../../style';
import styles from './style';

const maxLengthTitle = 90;

const DashboardNewsContent = () => {
  const navigate = useNavigate();

  const { userData } = useUserData();
  const { isUserNotOnThePinScreen } = useLoginPin();

  // infinite query manages pagination internally
  const { eventClickDetailPageStock, eventClickNews } = useEventTracker();
  const {
    data: results,
    isInitialLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useNewsFeedList({ pageSize: 50, enabled: isUserNotOnThePinScreen && !!userData?.user_info });
  const handleUpdateScrollbars = useCallback((values) => {
    const { scrollTop, scrollHeight, clientHeight } = values;
    const pad = 20; // 100px of the bottom
    // t will be greater than 1 if we are about to reach the bottom
    const t = (scrollTop + pad) / (scrollHeight - clientHeight);
    if (t > 1 && hasNextPage && !isFetchingNextPage && !isInitialLoading) {
      fetchNextPage();
    }
  });

  const onNewsClick = (news) => {
    const eventProps = {
      'entry point': 'homepage',
      'news id': news?.id,
      'stock ticker': news?.prices?.map((price) => `$${price.code}`).join(', '),
      'news source': news?.source,
    };
    eventClickNews(eventProps);
  };

  if (isInitialLoading && results.length === 0) {
    return (
      <Row>
        <Col className="text-center">
          <Loader style={{ margin: 24 }} />
        </Col>
      </Row>
    );
  }

  return (
    <Scrollbars
      className="table-shadow"
      style={styles.dataContainer}
      autoHide
      onUpdate={handleUpdateScrollbars}
    >
      <Col className="p-16">
        {results.map((news, idx) => {
          const prices = news.prices ? news.prices.slice(0, 3) : [];
          const { source } = news;
          return (
            <Col md={12} className="p-0" key={`news-${news.title}-${idx}`}>
              <Row noGutters className="mb-8 align-items-start">
                <Col className="p-0">
                  <a
                    href={news.link}
                    target="_blank"
                    className="cursor-pointer"
                    style={styles.newsTitle}
                    rel="noreferrer"
                    onClick={() => onNewsClick(news)}
                  >
                    <Text size="body-bold">
                      {news.title.length > maxLengthTitle
                        ? `${news.title.substr(0, maxLengthTitle).trim()}...`
                        : news.title}
                    </Text>
                  </a>
                </Col>
              </Row>
              <div>
                {source && (
                  <>
                    <a
                      href={news.link}
                      target="_blank"
                      className="cursor-pointer"
                      style={styles.newsTitle}
                      rel="noreferrer"
                      onClick={() => onNewsClick(news)}
                    >
                      <Text size="label-regular" color={stylesGlobal.neutral700}>
                        {source}
                      </Text>
                    </a>
                    <Text
                      size="label-regular"
                      color={stylesGlobal.neutral700}
                      className="pr-4 pl-4"
                    >
                      -
                    </Text>
                  </>
                )}
                <Text size="label-regular" moment color={stylesGlobal.neutral700}>
                  {news.pub_date}
                </Text>
              </div>
              {news.type === 'STOCK' && (
                <Row noGutters className="mb-24">
                  {prices.length > 0 && (
                    <div className="d-flex align-items-center mt-16">
                      <div className="mr-4">
                        <ChevronDropRight width={24} height={24} />
                      </div>
                      <div className="d-flex">
                        {prices.map((price) => (
                          <Pills key={`col-price-${price.code}`} className={NewsItemPills} disabled>
                            <div
                              className="cursor-pointer"
                              onClick={() => {
                                navigate(`/home/saham/${price.code}`);
                                const eventProps = {
                                  'stock name': price.code,
                                };
                                eventClickDetailPageStock(eventProps);
                              }}
                            >
                              <Text bold className={NewsItemPillsText}>
                                {price.code}
                              </Text>{' '}
                              <Text bold className={NewsItemPillsTextNeutral(price.change_pct)}>
                                <FormatPercentage value={price.change_pct * 100} />
                              </Text>
                            </div>
                          </Pills>
                        ))}
                      </div>
                    </div>
                  )}
                </Row>
              )}
              <Col className="pl-0 pr-0">
                <Separator bottom={16} color={stylesGlobal.neutral50} />
              </Col>
            </Col>
          );
        })}
        {hasNextPage && isFetchingNextPage && (
          <div className="text-center">
            <Loader style={{ margin: 24 }} />
          </div>
        )}
      </Col>
    </Scrollbars>
  );
};

export default DashboardNewsContent;
